import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FilterOutlined, ReloadOutlined, ScheduleOutlined } from '@ant-design/icons';
import { Button, Card, Form, Layout, Modal, PageHeader, Select, Table } from 'antd';
import { apiClient, apiErrorMessage, canViewAllSmats } from '../../utils';
import { Area, Location, Risk, Smat, User } from '../../models';
import moment from 'moment';
import { SmatAuditPage } from './smat-audit-page';
import { SmatStatus } from './smat-status';
import { UserContext } from '../contexts';
import { useForm } from 'antd/lib/form/Form';

export function SmatsPage() {
  const { Content } = Layout;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [smats, setSmats] = useState<Smat[]>();
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditModalSmatId, setAuditModalSmatId] = useState<string>();
  const [scope, setScope] = useState('user');
  const [areaSmatsAvailable, setAreaSmatsAvialable] = useState(false);
  const [allSmatsAvailable, setAllSmatsAvialable] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<any>(undefined);

  useEffect(() => {
    setAllSmatsAvialable(canViewAllSmats(user));
    setAreaSmatsAvialable(!!user?.areas?.length);
  }, [user]);

  const loadSmats = useCallback(() => {
    if (!scope) {
      return;
    }
    setLoading(true);

    const queryParams = [`year=2023`, `scope=${scope}`];
    if (filterValues) {
      if (filterValues.status && filterValues.status !== 'all') {
        queryParams.push(`status=${filterValues.status}`);
      }
      if (filterValues.week && filterValues.week !== 'all') {
        queryParams.push(`week=${filterValues.week}`);
      }
      if (filterValues.locationId && filterValues.locationId !== 'all') {
        queryParams.push(`locationId=${filterValues.locationId}`);
      }
    }
    const queryParamsStr = queryParams.join('&');

    apiClient
      .get(`smats?${queryParamsStr}`)
      .then(response => {
        setSmats(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  }, [scope, filterValues]);

  useEffect(() => {
    loadSmats();
  }, [loadSmats, scope]);

  const onRefreshClick = () => {
    loadSmats();
  };

  const onFiltersToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const onFiltersChange = (filterValues: any) => {
    setFilterValues(filterValues);
  };

  const onSmatChanged = () => {
    loadSmats();
  };

  const openAuditModal = (smatId: string) => {
    setAuditModalSmatId(smatId);
    setAuditModalOpen(true);
  };

  const closeAuditModal = () => {
    setAuditModalOpen(false);
    setAuditModalSmatId(undefined);
  };

  const onScopeChange = (value: string) => {
    setScope(value);
  };

  const tableColumns: any[] = [
    {
      title: 'SMAT no',
      dataIndex: 'smatNo',
      key: 'smatNo',
      render: (smatNo: string, smat: Smat) => (
        <Button type="link" onClick={() => openAuditModal(smat.id)}>
          {smatNo}
        </Button>
      ),
    },
    {
      title: 'Hafta',
      dataIndex: 'week',
      key: 'week',
    },
    {
      title: 'Alan',
      dataIndex: 'area',
      key: 'area',
      render: (area: Area) => area.code + ' - ' + area.name,
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (startDate: string) => moment(startDate).format('DD.MM.yyyy'),
    },
    {
      title: 'Bitiş',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (endDate: string) => moment(endDate).format('DD.MM.yyyy'),
    },
    {
      title: 'Risk sayısı',
      dataIndex: 'risks',
      key: 'risks',
      render: (risks: Risk[]) => (risks.length ? risks.length : '-'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: number, smat: Smat) => <SmatStatus smat={smat} />,
    },
    {
      title: 'Alan sorumlusu',
      dataIndex: ['area', 'responsibleUser'],
      key: 'responsibleUser',
      render: (responsibleUser: User) => responsibleUser?.fullNameId,
    },
    {
      title: 'Denetleyici',
      dataIndex: 'user',
      key: 'user',
      render: (user: User) => user.fullNameId,
    },
    {
      title: 'Tamamlayan',
      dataIndex: 'completedBy',
      key: 'completedBy',
      render: (completedBy: User) => {
        return completedBy ? completedBy.fullNameId : '-';
      },
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <ScheduleOutlined /> SMAT'lar
          </>
        }
        extra={[
          <Button key="filters" onClick={onFiltersToggle} icon={<FilterOutlined />}>
            Filtreleri {filtersOpen ? 'gizle' : 'göster'}
          </Button>,
          <>
            {(areaSmatsAvailable || allSmatsAvailable) && (
              <Select style={{ width: 150, marginLeft: '1rem' }} value={scope} onChange={onScopeChange}>
                <Select.Option value="user">Denetlediklerim</Select.Option>
                {areaSmatsAvailable && <Select.Option value="area">Alanlarım</Select.Option>}
                {allSmatsAvailable && <Select.Option value="all">Tümü</Select.Option>}
              </Select>
            )}
          </>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
        ]}
      />
      <Content>
        <FiltersForm hidden={!filtersOpen} onChange={onFiltersChange} />
        <Table columns={tableColumns} dataSource={smats} rowKey="id" loading={loading} pagination={false} />
      </Content>
      <Modal title="SMAT denetleme" visible={auditModalOpen} footer={null} onCancel={closeAuditModal} destroyOnClose={true} width="90%">
        <SmatAuditPage smatId={auditModalSmatId} onSaved={onSmatChanged} />
      </Modal>
    </>
  );
}

function FiltersForm(props: { hidden: boolean; onChange: (filterValues: any) => void }) {
  const [weeks, setWeeks] = useState<number[]>();
  const [locations, setLocations] = useState<Location[]>();
  const [locationsLoading, setLocationsLoading] = useState(false);

  const { Option } = Select;
  const [form] = useForm();

  useEffect(() => {
    apiClient
      .get(`locations`)
      .then(response => {
        setLocations(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLocationsLoading(false);
      });
  }, []);

  useEffect(() => {
    const newWeeks = [];
    for (let i = 1; i <= 52; i++) {
      newWeeks.push(i);
    }
    setWeeks(newWeeks);
  }, []);

  const onChange = () => {
    const values = form.getFieldsValue();
    props.onChange(values);
  };

  if (props.hidden) {
    return null;
  }

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <Form form={form} layout="inline">
        <Form.Item name="status">
          <Select allowClear placeholder="Statü" style={{ width: '12rem' }} onChange={onChange}>
            <Option value="all">Tümü</Option>
            <Option value="0">Planlandı</Option>
            <Option value="1">Aktif</Option>
            <Option value="2-3">Tamamlandı</Option>
            <Option value="2">Tamamlandı (Zamanında)</Option>
            <Option value="3">Tamamlandı (Geç)</Option>
            <Option value="4">Tamamlanmadı</Option>
          </Select>
        </Form.Item>
        <Form.Item name="locationId">
          <Select allowClear placeholder="Lokasyon" style={{ width: '12rem' }} loading={locationsLoading} onChange={onChange}>
            <Option value="all">Tümü</Option>
            {locations?.map(l => (
              <Option value={l.id}>{l.name}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="week">
          <Select
            allowClear
            showSearch
            placeholder="Hafta"
            style={{ width: '12rem' }}
            onChange={onChange}
            filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            <Option key="weeks_all" value="all">
              Tümü
            </Option>
            {weeks?.map(w => (
              <Option key={w} value={w}>
                {w}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Card>
  );
}
